import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiSave, FiX, FiPercent, FiHash } from 'react-icons/fi';
import { formatCurrency } from '../../../utils/formatters';

const CalculationItemForm = ({
  item = null,
  calculationId,
  onSave,
  onCancel,
  loading = false,
  error = null
}) => {
  const { t } = useTranslation();
  const { 
    register, 
    handleSubmit, 
    reset, 
    setValue,
    watch,
    formState: { errors } 
  } = useForm({
    defaultValues: {
      productCode: '',
      productDescription: '',
      quantity: 1,
      listPrice: 0,
      buyPrice: 0,
      stockPrice: 0,
      sellPrice: 0,
      customsValue: 0,
      customsAvailability: false
    }
  });

  // Watch values for calculations
  const quantity = watch('quantity', 1);
  const sellPrice = watch('sellPrice', 0);

  // Calculate total
  const total = (parseFloat(quantity) || 0) * (parseFloat(sellPrice) || 0);

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      reset({
        productCode: item.productCode || '',
        productDescription: item.productDescription || '',
        quantity: item.quantity || 1,
        listPrice: item.listPrice || 0,
        buyPrice: item.buyPrice || 0,
        stockPrice: item.stockPrice || 0,
        sellPrice: item.sellPrice || 0,
        customsValue: item.customsValue || 0,
        customsAvailability: item.customsAvailability || false
      });
    } else {
      reset({
        productCode: '',
        productDescription: '',
        quantity: 1,
        listPrice: 0,
        buyPrice: 0,
        stockPrice: 0,
        sellPrice: 0,
        customsValue: 0,
        customsAvailability: false
      });
    }
  }, [item, reset]);

  const onSubmit = (data) => {
    const transformedData = {};
    
    // For new items (CREATE), use direct field values without {value: ...} wrapper
    if (!item) {
      if (data.productCode && data.productCode.trim()) {
        transformedData.productCode = data.productCode.trim();
      }
      if (data.productDescription && data.productDescription.trim()) {
        transformedData.productDescription = data.productDescription.trim();
      }
      if (data.quantity !== undefined && data.quantity !== null) {
        transformedData.quantity = parseFloat(data.quantity) || 0;
      }
      if (data.listPrice !== undefined && data.listPrice !== null) {
        transformedData.listPrice = parseFloat(data.listPrice) || 0;
      }
      if (data.buyPrice !== undefined && data.buyPrice !== null) {
        transformedData.buyPrice = parseFloat(data.buyPrice) || 0;
      }
      if (data.stockPrice !== undefined && data.stockPrice !== null) {
        transformedData.stockPrice = parseFloat(data.stockPrice) || 0;
      }
      if (data.sellPrice !== undefined && data.sellPrice !== null) {
        transformedData.sellPrice = parseFloat(data.sellPrice) || 0;
      }
      if (data.customsValue !== undefined && data.customsValue !== null) {
        transformedData.customsValue = parseFloat(data.customsValue) || 0;
      }
      if (data.customsAvailability !== undefined) {
        transformedData.customsAvailability = Boolean(data.customsAvailability);
      }
    } else {
      // For existing items, only include fields that have changed
      if (data.productCode !== item.productCode && data.productCode && data.productCode.trim()) {
        transformedData.productCode = { value: data.productCode.trim() };
      }
      
      if (data.productDescription !== item.productDescription && data.productDescription && data.productDescription.trim()) {
        transformedData.productDescription = { value: data.productDescription.trim() };
      }
      
      if (parseFloat(data.quantity) !== parseFloat(item.quantity || 0)) {
        transformedData.quantity = { value: parseFloat(data.quantity) || 0 };
      }
      
      if (parseFloat(data.listPrice) !== parseFloat(item.listPrice || 0)) {
        transformedData.listPrice = { value: parseFloat(data.listPrice) || 0 };
      }
      
      if (parseFloat(data.buyPrice) !== parseFloat(item.buyPrice || 0)) {
        transformedData.buyPrice = { value: parseFloat(data.buyPrice) || 0 };
      }
      
      if (parseFloat(data.stockPrice) !== parseFloat(item.stockPrice || 0)) {
        transformedData.stockPrice = { value: parseFloat(data.stockPrice) || 0 };
      }
      
      if (parseFloat(data.sellPrice) !== parseFloat(item.sellPrice || 0)) {
        transformedData.sellPrice = { value: parseFloat(data.sellPrice) || 0 };
      }
      
      if (parseFloat(data.customsValue) !== parseFloat(item.customsValue || 0)) {
        transformedData.customsValue = { value: parseFloat(data.customsValue) || 0 };
      }
      
      if (Boolean(data.customsAvailability) !== Boolean(item.customsAvailability)) {
        transformedData.customsAvailability = { value: Boolean(data.customsAvailability) };
      }
    }
    
    console.log('Sending only changed fields:', transformedData);
    onSave(transformedData);
  };

  // Handle numeric input with proper parsing
  const handleNumericChange = (e, fieldName) => {
    const value = parseFloat(e.target.value) || 0;
    setValue(fieldName, value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6 bg-white rounded-lg">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error.message || t('calculation.failedToSave')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Product Code */}
        <div>
          <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.productCode')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="text"
              id="productCode"
              {...register('productCode', { required: t('calculation.productCode') + ' ' + t('common.required').toLowerCase() })}
              className={`block w-full rounded-md ${errors.productCode ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500'} sm:text-sm`}
              disabled={loading}
              placeholder={t('calculation.productCode')}
            />
          </div>
          {errors.productCode && (
            <p className="mt-1 text-sm text-red-600">{errors.productCode.message}</p>
          )}
        </div>

        {/* Product Description */}
        <div>
          <label htmlFor="productDescription" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.productDescription')}
          </label>
          <div className="mt-1">
            <input
              type="text"
              id="productDescription"
              {...register('productDescription')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder={t('calculation.productDescription')}
              disabled={loading}
            />
          </div>
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.quantity')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiHash className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              id="quantity"
              step="0.001"
              min="0"
              {...register('quantity', { 
                required: t('calculation.quantity') + ' ' + t('common.required').toLowerCase(),
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'quantity')}
              className={`block w-full pl-10 pr-12 sm:text-sm rounded-md ${errors.quantity ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.000"
              disabled={loading}
            />
          </div>
          {errors.quantity && (
            <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
          )}
        </div>

        {/* List Price */}
        <div>
          <label htmlFor="listPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.listPrice')}
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 text-lg font-semibold">€</span>
            </div>
            <input
              type="number"
              id="listPrice"
              step="0.01"
              min="0"
              {...register('listPrice', { 
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'listPrice')}
              className={`block w-full pl-10 pr-3 sm:text-sm rounded-md ${errors.listPrice ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {errors.listPrice && (
            <p className="mt-1 text-sm text-red-600">{errors.listPrice.message}</p>
          )}
        </div>

        {/* Buy Price */}
        <div>
          <label htmlFor="buyPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.buyPrice')}
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 text-lg font-semibold">€</span>
            </div>
            <input
              type="number"
              id="buyPrice"
              step="0.01"
              min="0"
              {...register('buyPrice', { 
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'buyPrice')}
              className={`block w-full pl-10 pr-3 sm:text-sm rounded-md ${errors.buyPrice ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {errors.buyPrice && (
            <p className="mt-1 text-sm text-red-600">{errors.buyPrice.message}</p>
          )}
        </div>

        {/* Stock Price */}
        <div>
          <label htmlFor="stockPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.stockPrice')}
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 text-lg font-semibold">€</span>
            </div>
            <input
              type="number"
              id="stockPrice"
              step="0.01"
              min="0"
              {...register('stockPrice', { 
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'stockPrice')}
              className={`block w-full pl-10 pr-3 sm:text-sm rounded-md ${errors.stockPrice ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {errors.stockPrice && (
            <p className="mt-1 text-sm text-red-600">{errors.stockPrice.message}</p>
          )}
        </div>

        {/* Sell Price */}
        <div>
          <label htmlFor="sellPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.sellPrice')} *
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 text-lg font-semibold">€</span>
            </div>
            <input
              type="number"
              id="sellPrice"
              step="0.01"
              min="0"
              {...register('sellPrice', { 
                required: t('calculation.sellPrice') + ' ' + t('common.required').toLowerCase(),
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'sellPrice')}
              className={`block w-full pl-10 pr-3 sm:text-sm rounded-md ${errors.sellPrice ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {errors.sellPrice && (
            <p className="mt-1 text-sm text-red-600">{errors.sellPrice.message}</p>
          )}
        </div>

        {/* Customs Value */}
        <div>
          <label htmlFor="customsValue" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.customsValue')}
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="number"
              id="customsValue"
              step="0.01"
              min="0"
              {...register('customsValue', { 
                min: { value: 0, message: t('common.required') },
                valueAsNumber: true
              })}
              onChange={(e) => handleNumericChange(e, 'customsValue')}
              className={`block w-full sm:text-sm rounded-md ${errors.customsValue ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
              placeholder="0.00"
              disabled={loading}
            />
          </div>
          {errors.customsValue && (
            <p className="mt-1 text-sm text-red-600">{errors.customsValue.message}</p>
          )}
        </div>

        {/* Customs Availability */}
        <div>
          <label htmlFor="customsAvailability" className="block text-sm font-medium text-gray-700 mb-1">
            {t('calculation.customsAvailability')}
          </label>
          <div className="mt-1">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                id="customsAvailability"
                {...register('customsAvailability')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-600">{t('calculation.customsAvailability')}</span>
            </label>
          </div>
        </div>

        {/* Summary Box */}
        <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">{t('common.summary')}</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('calculation.quantity')}:</span>
              <span className="font-medium">{quantity}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('calculation.sellPrice')}:</span>
              <span className="font-medium">{formatCurrency(sellPrice)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold border-t border-gray-200 pt-2 mt-2">
              <span>{t('common.total')}:</span>
              <span className="text-blue-600">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiX className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.saving')}
            </>
          ) : (
            <>
              <FiSave className="-ml-1 mr-2 h-5 w-5" />
              {item ? t('calculation.editItem') : t('calculation.addItem')}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CalculationItemForm;
